import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { useMemo } from "react";
import { ensureInteger } from "../../common/utils/generalUtils";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const prepareArray = data => ensureArray(data).map(member => ({
    ...member,
    name: member.preferredname ?? member.firstname,
    fullname: (member.preferredname ?? member.firstname) + ' ' + member.lastname,
    // TODO remove once customtables is deprecated needed for ACES computed fields like classification
    customfields: { ...member.customfields, ...member.customtables },
}))


export const memberClientSlice = createSlice({
    name: 'clientMembers',
    initialState: {
        data: {},
        inactiveMembers: {},
    },
    reducers: {
        setActive: (state, action) => {
            // we can do the following because we always fetch all the members
            state.data = {};
            prepareArray(action.payload).forEach(m => state.data[m.id] = m);
        },
        setInactive : (state, action) => {
            state.inactiveMembers = {};
            prepareArray(action.payload).forEach(m => state.inactiveMembers[m.id] = m);
        },
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareArray(action.payload.data).forEach(m => {
                const id = ensureInteger(m.id)
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
                toProcess.delete(m.id);
            });
            toProcess.forEach(id => {
                delete state.data[id];
                delete state.inactiveMembers[id];
            });
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m;
                } else {
                    state.data[id] = m;
                    delete state.inactiveMembers[id];
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(m => {
                const id = ensureInteger(m.id);
                if (state.data[id] && m.inactive) {
                    delete state.data[id];
                    state.inactiveMembers[id] = m
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberClientSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staff_one', action, data => ({ name: `${data.firstname} ${data.lastname}`,
    id: data.id 
}));
const visualizeFailure = verb => DefaultFailureAction(verb, 'staff_one');

const fetchAllActiveMembers = network => network.get('/api/v2/clientMembers/active', memberClientSlice.actions.setActive, visualizeFailure('get'));
const fetchAllInactiveMembers = network => network.get('/api/v2/clientMembers/inactive' , memberClientSlice.actions.setInactive, visualizeFailure('get')); 
const fetchMember = aggregatingFetch(
    id => network => network.post('/api/v2/clientMembers', [id], data => memberClientSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/clientMembers', Array.from(ids), data => memberClientSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
// #endregion

// #region selectors
const selectMemberData = state => state.clientMembers.data;
const selectAllMemberData = state => state.clientMembers;
const selectMembersById = ids => createSelector([selectAllMemberData], data => ids.reduce((res, id) => { let m = data.data[id] ?? data.inactiveMembers[id]; if (m) { res.push(m) } return res }, []));
const selectMemberById = id => createSelector([selectAllMemberData], data => data.data[id] ?? data.inactiveMembers[id]);
const selectAllActiveMembers = createSelector([selectMemberData], (members) => Object.values(members));
const selectInactiveMemberData = state => state.clientMembers.inactiveMembers;
const selectAllInactiveMembers = createSelector([selectInactiveMemberData], (members) => Object.values(members));

const selectAllActiveMembersMap = createSelector([selectMemberData], data => data);
const selectMemberMapById = ids => createSelector([selectMemberData], data => {
    return ids.reduce((acc, id) => {
        const memberData = data[id]
        if (memberData) {
            acc[id] = memberData
        } 
        return acc;
    }, {});
});
// #endregion

const listen = {
    memberClientCreated: (member, { dispatch, websocket }) => {
        markFetched(websocket, 'member', member.id);
        dispatch(memberClientSlice.actions.addOrUpdate(member))
    },
    memberClientUpdated: (member, { dispatch }) => dispatch(memberClientSlice.actions.addOrUpdate(member)),
    memberClientRemoved: (member, { dispatch }) => dispatch(memberClientSlice.actions.remove(member)),
}

// #endregion

const options = { condition: credentials => credentials?.hasRole('client roster viewer') };
const singleOptions = { ...options, defaultValue: null}

const initMemberByID = initMapFactory(fetchMember, listen);
registerEntityBasedFetch('member', initMemberByID);

const initMultiMemberByID = initMultiMapFactory(initMemberByID, listen);
const initActiveMembers = queryMapFactory(fetchAllActiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'activeStaffMembers', })();
const initInactiveMembers = queryMapFactory(fetchAllInactiveMembers, 'id', initMemberByID, listen, { allowEmpty: true, name: 'inactiveStaffMembers'})();
export const [useClientAllActiveMembers] = dataHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, options);
export const [useClientAllInactiveMembers] = dataHookWithCredentialsFactory(selectAllInactiveMembers, initInactiveMembers, options);
export const [useClientMembers] = dataHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, options);
export const [useClientMember, realizeClientMember] = dataHookWithCredentialsFactory(selectMemberById, initMemberByID, singleOptions);
export const [useClientMembersMap] = dataHookWithCredentialsFactory(selectMemberMapById, initMultiMapFactory(initMemberByID, listen, options));
export const [useClientAllActiveMemberMap] = dataHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, options);

export const useAllMembers = () => {
    const activeMembers = useClientAllActiveMembers();
    const inactiveMembers = useClientAllInactiveMembers();

    return useMemo(() => [...activeMembers, ...inactiveMembers], [activeMembers, inactiveMembers]);
}

export const [useReadClientMember] = readonlyHookWithCredentialsFactory(selectMemberById, initMemberByID, singleOptions);
export const [useReadClientMembers] = readonlyHookWithCredentialsFactory(selectMembersById, initMultiMemberByID, options);
export const [useReadClientAllActiveMembers] = readonlyHookWithCredentialsFactory(selectAllActiveMembers, initActiveMembers, options);
export const [useReadClientAllActiveMembersMap] = readonlyHookWithCredentialsFactory(selectAllActiveMembersMap, initActiveMembers, options);

const reducer = { name: memberClientSlice.name, reducer: memberClientSlice.reducer }
export default reducer;